'use client';

import {
  CheckCircle,
  CircleNotch,
  Info,
  Warning,
  WarningCircle,
} from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { Toaster as Sonner } from 'sonner';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { resolvedTheme } = useTheme();

  return (
    <Sonner
      theme={(resolvedTheme ?? 'light') as ToasterProps['theme']}
      className="toaster group"
      position="bottom-right"
      offset={20}
      gap={10}
      style={{ fontFamily: 'var(--ds-font-body)' }}
      icons={{
        success: (
          <CheckCircle
            weight="fill"
            // eslint-disable-next-line no-restricted-syntax -- a toast IS the transient system feedback ds-success-* is reserved for
            className="size-4 text-ds-success-default"
          />
        ),
        error: (
          <WarningCircle
            weight="fill"
            className="size-4 text-ds-danger-default"
          />
        ),
        warning: (
          <Warning weight="fill" className="size-4 text-ds-warning-default" />
        ),
        info: <Info weight="fill" className="size-4 text-ds-blue-subtle" />,
        loading: (
          <CircleNotch className="size-4 animate-spin text-ds-text-subtle" />
        ),
      }}
      toastOptions={{
        // Sonner's own [data-button] rule ties our utility classes on
        // specificity, so button colors go through inline vars instead.
        actionButtonStyle: {
          backgroundColor: 'var(--ds-brand-default)',
          color: 'var(--ds-surface-white)',
        },
        cancelButtonStyle: {
          backgroundColor: 'var(--ds-gray-subtlest)',
          color: 'var(--ds-text-default)',
        },
        classNames: {
          toast:
            'group toast group-[.toaster]:items-start group-[.toaster]:gap-2.5 group-[.toaster]:rounded-lg group-[.toaster]:border group-[.toaster]:border-ds-border-subtlest group-[.toaster]:bg-ds-surface-white group-[.toaster]:p-3.5 group-[.toaster]:shadow-md',
          icon: 'group-[.toast]:mt-0.5 group-[.toast]:self-start',
          title:
            'group-[.toast]:ds-text-body-sm-medium group-[.toast]:text-ds-text-darker',
          description:
            'group-[.toast]:ds-text-body-xs-medium group-[.toast]:text-ds-text-subtle',
          actionButton:
            'group-[.toast]:ds-text-body-xs-medium group-[.toast]:rounded-md group-[.toast]:bg-ds-brand-default group-[.toast]:px-2.5 group-[.toast]:py-1 group-[.toast]:text-ds-surface-white',
          cancelButton:
            'group-[.toast]:ds-text-body-xs-medium group-[.toast]:rounded-md group-[.toast]:bg-ds-gray-subtlest group-[.toast]:px-2.5 group-[.toast]:py-1 group-[.toast]:text-ds-text-default',
          closeButton:
            'group-[.toast]:border-ds-border-subtler group-[.toast]:bg-ds-surface-white group-[.toast]:text-ds-text-subtle group-[.toast]:hover:text-ds-text-darker',
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
